import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CollectStep, FlowStep } from "../../../../types";

const FLEXIBILITY_LABELS: Record<string, string> = {
  low: "Bassa",
  medium: "Media",
  high: "Alta",
};

const FLEXIBILITY_DESCRIPTIONS: Record<string, string> = {
  low: "Il bot segue la domanda esattamente come scritta. Adatto per dati critici (importi, date contrattuali).",
  medium: "Il bot gestisce risposte fuori tema e riformula la domanda se l'utente non capisce. Adatto per dati anagrafici.",
  high: "Come Media, più il bot può dedurre il valore da informazioni già raccolte senza chiedere di nuovo. Adatto per preferenze e disponibilità.",
};

const schema = z.object({
  field_key: z.string().min(1, "Campo obbligatorio"),
  field_type: z.enum(["text", "number", "date", "boolean", "choice"]),
  question: z.string().min(1, "Domanda obbligatoria"),
  validation_regex: z.string().optional(),
  required: z.boolean(),
  error_message: z.string().optional(),
  max_iterations: z.number().int().min(1).max(10).optional(),
  next_step_id: z.string().optional(),
  conversation_flexibility: z.enum(["low", "medium", "high"]).optional(),
  order: z.number().int().min(0),
});

type FormData = z.infer<typeof schema>;

interface Props {
  step: CollectStep;
  steps: FlowStep[];
  onSave: (updated: CollectStep) => void;
}

export const CollectStepForm = ({ step, steps, onSave }: Props) => {
  const [choices, setChoices] = useState<string[]>(step.choices ?? []);
  const [choiceInput, setChoiceInput] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      field_key: step.field_key,
      field_type: step.field_type,
      question: step.question,
      validation_regex: step.validation_regex ?? "",
      required: step.required,
      error_message: step.error_message ?? "",
      max_iterations: step.max_iterations ?? 5,
      next_step_id: step.next_step_id ?? "",
      conversation_flexibility: step.conversation_flexibility ?? "low",
      order: step.order,
    },
  });

  const fieldType = watch("field_type");
  const flexibility = watch("conversation_flexibility");

  useEffect(() => {
    reset({
      field_key: step.field_key,
      field_type: step.field_type,
      question: step.question,
      validation_regex: step.validation_regex ?? "",
      required: step.required,
      error_message: step.error_message ?? "",
      max_iterations: step.max_iterations ?? 5,
      next_step_id: step.next_step_id ?? "",
      conversation_flexibility: step.conversation_flexibility ?? "low",
      order: step.order,
    });
    setChoices(step.choices ?? []);
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit((data) => {
    onSave({
      ...step,
      ...data,
      choices: data.field_type === "choice" ? choices : undefined,
      validation_regex: data.validation_regex || undefined,
      error_message: data.error_message || undefined,
      next_step_id: data.next_step_id || undefined,
      conversation_flexibility: data.conversation_flexibility ?? "low",
    });
  });

  const addChoice = () => {
    const trimmed = choiceInput.trim();
    if (trimmed && !choices.includes(trimmed)) {
      setChoices((prev) => [...prev, trimmed]);
    }
    setChoiceInput("");
  };

  const removeChoice = (c: string) => setChoices((prev) => prev.filter((x) => x !== c));

  return (
    <form onSubmit={onSubmit} className="flex-col gap-2">
      <div className="flex-row gap-2">
        <Controller
          name="field_key"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              label="Field Key"
              placeholder="es. nome, eta, professione"
              error={!!errors.field_key}
              helperText={errors.field_key?.message}
            />
          )}
        />
        <Controller
          name="field_type"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small" error={!!errors.field_type}>
              <InputLabel>Tipo campo</InputLabel>
              <Select {...field} label="Tipo campo">
                <MenuItem value="text">text</MenuItem>
                <MenuItem value="number">number</MenuItem>
                <MenuItem value="date">date</MenuItem>
                <MenuItem value="boolean">boolean</MenuItem>
                <MenuItem value="choice">choice</MenuItem>
              </Select>
              {errors.field_type && (
                <FormHelperText>{errors.field_type.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </div>

      <Controller
        name="question"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Domanda"
            placeholder="Qual è il tuo nome?"
            error={!!errors.question}
            helperText={errors.question?.message}
          />
        )}
      />

      {fieldType === "choice" && (
        <Box>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              size="small"
              label="Aggiungi opzione"
              value={choiceInput}
              onChange={(e) => setChoiceInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChoice())}
            />
            <Button variant="outlined" size="small" onClick={addChoice}>
              Aggiungi
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {choices.map((c) => (
              <Chip key={c} label={c} size="small" onDelete={() => removeChoice(c)} />
            ))}
          </Box>
        </Box>
      )}

      <div className="flex-row gap-2">
        <Controller
          name="validation_regex"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              label="Regex validazione (opzionale)"
              placeholder="^\d+$"
            />
          )}
        />
        <Controller
          name="error_message"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              label="Messaggio errore (opzionale)"
            />
          )}
        />
      </div>

      <div className="flex-row gap-2">
        <Controller
          name="max_iterations"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => field.onChange(Number(e.target.value))}
              size="small"
              type="number"
              label="Max tentativi"
              inputProps={{ min: 1, max: 10 }}
              sx={{ maxWidth: 160 }}
              error={!!errors.max_iterations}
              helperText={errors.max_iterations?.message}
            />
          )}
        />
        <Controller
          name="required"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Obbligatorio"
            />
          )}
        />
      </div>

      <Controller
        name="conversation_flexibility"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel>Flessibilità conversazionale</InputLabel>
            <Select {...field} label="Flessibilità conversazionale" value={field.value ?? "low"}>
              {(["low", "medium", "high"] as const).map((v) => (
                <MenuItem key={v} value={v}>
                  {FLEXIBILITY_LABELS[v]}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {FLEXIBILITY_DESCRIPTIONS[field.value ?? "low"]}
            </FormHelperText>
          </FormControl>
        )}
      />

      {(flexibility === "medium" || flexibility === "high") && (watch("max_iterations") ?? 3) < 2 && (
        <Alert severity="warning" sx={{ fontSize: "0.8rem", py: 0.5 }}>
          Con flessibilità Media o Alta, max tentativi ≥ 2 consente al bot di riformulare la domanda.
        </Alert>
      )}

      <Controller
        name="next_step_id"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel>Step successivo (opzionale)</InputLabel>
            <Select
              {...field}
              label="Step successivo (opzionale)"
              value={field.value ?? ""}
            >
              <MenuItem value="">— nessuno —</MenuItem>
              {steps
                .filter((s) => s.id !== step.id)
                .map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    [{s.type}] {s.id.slice(0, 8)}… (ord. {s.order})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      />

      <Box>
        <Button variant="contained" type="submit" size="small">
          Salva step
        </Button>
      </Box>
    </form>
  );
};
